import { useForm, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'lib/translations/i18next'
import { validateBusinessEmail } from './helpers/validateBusinessEmail/validateBusinessEmail'
import { isPaidAdCampaign } from './helpers/isPaidAdCampaign/isPaidAdCampaign'
import { SignupInterestsEnum, BusinessTypeEnum } from 'gql/types'

const productPageSchema = z.object({
  email: z.email({ error: t('emailIsIncorrect') }),
  businessType: z.enum([BusinessTypeEnum.PAYER, BusinessTypeEnum.ACCOUNTING_PARTNER]),
  interestedIn: z
    .object({
      billPay: z.boolean().nullish(),
      workingCapital: z.boolean().nullish(),
      purchasing: z.boolean(),
      landedCosts: z.boolean().nullish(),
    })
    .refine((interestedIn) => Object.values(interestedIn).some((value) => value)),
})
const getSchema = (isPaidAdCampaign: boolean) => {
  if (isPaidAdCampaign) {
    return productPageSchema.omit({ email: true }).extend({
      email: z
        .email({ error: t('emailIsIncorrect') })
        .refine(validateBusinessEmail, { error: t('validation::useBusinessEmail') }),
    })
  }
  return productPageSchema
}

export type ProductPageFormValues = z.infer<typeof productPageSchema>
interface Params {
  email: string | null
  referred_by?: Maybe<string>
  utm_source?: Maybe<string>
  utm_medium?: Maybe<string>
  utm_campaign?: Maybe<string>
  interestedIn: SignupInterestsEnum[]
  businessType: BusinessTypeEnum
}

export const useProductForm = ({
  businessType,
  email,
  interestedIn,
  utm_medium,
  utm_source,
  utm_campaign,
  referred_by,
}: Params) => {
  const isShopifyLead = referred_by === 'shopify'
  const showWcOption = (() => {
    const targetedCampaigns = [
      '21124180551',
      '21050006856',
      '20869794601',
      '21050006859',
      '20711294847',
      'FLI_Scale_AP',
    ]

    if (utm_campaign && targetedCampaigns.includes(utm_campaign)) {
      return false
    }

    if (isShopifyLead) {
      return false
    }

    return true
  })()

  const showBillPayOption = isShopifyLead ? false : true
  const showLandedCostsOption = isShopifyLead ? false : true
  const purchasingDefaultValue =
    interestedIn.includes(SignupInterestsEnum.PURCHASING) ||
    (!showBillPayOption && !showWcOption && !showLandedCostsOption)

  const form = useForm<ProductPageFormValues>({
    defaultValues: {
      email: email ?? '',
      interestedIn: {
        purchasing: purchasingDefaultValue,
        ...(showBillPayOption ? { billPay: interestedIn.includes(SignupInterestsEnum.BILL_PAY) } : {}),
        ...(showWcOption ? { workingCapital: interestedIn.includes(SignupInterestsEnum.WORKING_CAPITAL) } : {}),
        ...(showLandedCostsOption ? { landedCosts: interestedIn.includes(SignupInterestsEnum.LANDED_COSTS) } : {}),
      },
      businessType,
    },
    resolver: zodResolver(getSchema(isPaidAdCampaign({ utm_medium, utm_source }))),
  })

  return form
}

export const useProductFormContext = () => useFormContext<ProductPageFormValues>()
